import {
  Component,
  signal,
  output,
  input,
  inject,
  effect,
  computed,
} from '@angular/core';
import { SidebarMode } from './sidebar-mode.enum';
import {
  DashboardIconComponent,
  AdminIconComponent,
  OrganizationIconComponent,
  ProjectsIconComponent,
  FinancialIconComponent,
  ReportsIconComponent,
  ResourceManagementIconComponent,
  ReceiptTextIconComponent,
  ChevronDownIconComponent,
  ArrowLeftIconComponent,
  SignOutIconComponent,
  ContractsIconComponent,
} from '../icons';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface MenuItem {
  id: string;
  label: string;
  icon: string;
  route?: string;
  children?: MenuItem[];
}

@Component({
  selector: 'lib-sidebar',
  standalone: true,
  imports: [
    DashboardIconComponent,
    AdminIconComponent,
    OrganizationIconComponent,
    ProjectsIconComponent,
    FinancialIconComponent,
    ReportsIconComponent,
    ResourceManagementIconComponent,
    ReceiptTextIconComponent,
    ChevronDownIconComponent,
    ArrowLeftIconComponent,
    SignOutIconComponent,
    ContractsIconComponent,
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
})
export class SidebarComponent {
  // Input for menu items configuration
  menuItems = input<MenuItem[]>([]);
  mode = input<SidebarMode>(SidebarMode.Docked);

  private readonly router = inject(Router);

  // State signals
  isExpanded = signal<boolean>(true);
  activeRoute = signal<string>(this.router.url);
  openDropdowns = signal<Set<string>>(new Set());

  isDrawer = computed(() => this.mode() === SidebarMode.Drawer);
  showLabels = computed(() => this.isDrawer() || this.isExpanded());
  containerClass = computed(() =>
    this.isDrawer()
      ? 'w-full'
      : 'border border-border-neutral-subtle rounded-lg ' +
        (this.isExpanded() ? 'w-full md:w-64 lg:w-72' : 'w-20')
  );

  // Event outputs
  menuClick = output<MenuItem>();
  signOut = output<void>();

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.activeRoute.set(e.urlAfterRedirects));

    // Auto-expand parent dropdown for active child on init
    effect(() => {
      const items = this.menuItems();
      const currentUrl = this.activeRoute();
      for (const item of items) {
        if (
          item.children?.some(
            child => child.route && currentUrl.startsWith(child.route)
          )
        ) {
          this.openDropdowns.update(s => new Set(s).add(item.id));
          break;
        }
      }
    });
  }

  toggleSidebar(): void {
    this.isExpanded.update(current => !current);
  }

  toggleDropdown(menuId: string): void {
    if (!this.showLabels()) {
      this.isExpanded.set(true);
      this.openDropdowns.set(new Set([menuId]));
      return;
    }
    this.openDropdowns.update(current => {
      if (current.has(menuId)) {
        return new Set();
      }
      return new Set([menuId]);
    });
  }

  isDropdownOpen(menuId: string): boolean {
    return this.openDropdowns().has(menuId);
  }

  handleMenuClick(item: MenuItem): void {
    this.menuClick.emit(item);
  }

  handleSignOut(): void {
    this.signOut.emit();
  }

  isActive(item: MenuItem): boolean {
    return !!item.route && this.activeRoute().startsWith(item.route);
  }
}
